import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SelectorOverlay, ModalWrapper, ModalCloseButton, Button } from '../common';
import { destinationService, addressSearchService } from '../../api/services';
import { useDeepLTranslated } from '../../hooks/useDeepLTranslated';

/**
 * 병원 선택 — SelectorOverlay 기반
 */
export default function HospitalSelector({ value, onChange, placeholder, className = '', variant = 'default' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const effectivePlaceholder = placeholder || t('form.select_hospital');
  const regionSidebar = [
    { id: 'all', label: t('common.all_region') },
    { id: '서울', label: t('common.region_seoul') },
    { id: '경기', label: t('common.region_gyeonggi') },
  ];
  const regionLabels = {
    '서울': t('common.region_seoul'),
    '경기': t('common.region_gyeonggi'),
  };
  const [open, setOpen] = useState(false);
  const [activeRegion, setActiveRegion] = useState('all');
  const [search, setSearch] = useState('');
  const [rawHospitals, setRawHospitals] = useState([]);
  const [kakaoResults, setKakaoResults] = useState([]);
  const [kakaoLoading, setKakaoLoading] = useState(false);
  const [showRestrictionModal, setShowRestrictionModal] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    destinationService.getHospitals().then((data) => {
      if (cancelled) return;
      const recommended = (data || []).filter((h) => h.is_recommended === true);
      setRawHospitals(recommended.map((h) => ({
        value: String(h.id), label: h.name, region: h.region_name || h.region || '', address: h.address,
      })));
    }).catch((e) => console.warn(e.message));
    return () => { cancelled = true; };
  }, []);

  // Row 30: 병원 라벨/주소 render-time 번역 (AirportSelector와 동일 패턴)
  const labelBundle = useMemo(() => rawHospitals.map((h) => h.label), [rawHospitals]);
  const addressBundle = useMemo(() => rawHospitals.map((h) => h.address || ''), [rawHospitals]);
  const tLabels = useDeepLTranslated(labelBundle);
  const tAddresses = useDeepLTranslated(addressBundle);
  const allHospitals = useMemo(
    () => rawHospitals.map((h, i) => ({
      ...h,
      label: tLabels[i] || h.label,
      address: tAddresses[i] || h.address,
    })),
    [rawHospitals, tLabels, tAddresses]
  );

  useEffect(() => {
    const q = search.trim();
    if (!q) {
      setKakaoResults([]);
      setKakaoLoading(false);
      return;
    }
    setKakaoLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      addressSearchService.search(q, { categoryGroupCode: 'HP8' }).then((results) => {
        setKakaoResults(results);
        setKakaoLoading(false);
      }).catch(() => {
        setKakaoResults([]);
        setKakaoLoading(false);
      });
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const hospitalGroups = useMemo(() => [
    { key: '서울', label: '서울', items: allHospitals.filter((h) => h.region === '서울') },
    { key: '경기', label: '경기', items: allHospitals.filter((h) => h.region === '경기') },
  ], [allHospitals]);

  const displayLabel = useMemo(() => {
    if (!value) return null;
    const found = allHospitals.find((h) => h.value === value);
    return found ? found.label : value;
  }, [value, allHospitals]);

  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (q) {
      const matched = allHospitals.filter((h) => h.label.toLowerCase().includes(q));
      return { mode: 'search', results: matched };
    }

    if (activeRegion === 'all') {
      return { mode: 'grouped', groups: hospitalGroups };
    }

    const group = hospitalGroups.find((g) => g.key === activeRegion);
    return {
      mode: 'single',
      label: group?.label || '',
      items: group?.items || [],
    };
  }, [search, activeRegion, allHospitals, hospitalGroups]);

  const handleOpen = () => {
    if (!open) {
      setSearch('');
      setActiveRegion('all');
      setKakaoResults([]);
    }
    setOpen((v) => !v);
  };

  const handleSelect = (hospital) => {
    onChange(hospital.value, hospital.region, hospital.label || hospital.value);
    setOpen(false);
    setSearch('');
  };

  const handleKakaoSelect = (result) => {
    const isSeoul = result.address.startsWith('서울');
    const isGyeonggi = result.address.startsWith('경기');
    if (!isSeoul && !isGyeonggi) {
      setOpen(false);
      setShowRestrictionModal(true);
      return;
    }
    // BugHerd #98 follow-up: 카카오 검색 결과 선택 시에도 region 인자를 채워야
    // 부모(TransferHospital)의 hospitalRegion state 가 설정되고 BE 가 HospitalRegion
    // 가격을 매칭. 이전엔 isSeoul/isGyeonggi 판별만 하고 region 인자에 ''를 보내
    // BE 가 destination_region=병원풀네임 으로 받아 fallback 99,900 적용.
    const region = isSeoul ? '서울' : '경기';
    onChange(result.name, region, result.name);
    setOpen(false);
    setSearch('');
  };

  const renderItem = (h) => {
    const isSelected = h.value === value;
    return (
      <button
        key={h.value}
        type="button"
        onClick={() => handleSelect(h)}
        className={`text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
          isSelected
            ? 'text-orange-500 font-semibold bg-orange-50'
            : 'text-mono-700 hover:bg-mono-50 hover:text-mono-900'
        }`}
      >
        {h.label}
      </button>
    );
  };

  return (
    <>
    <SelectorOverlay
      displayLabel={displayLabel}
      placeholder={effectivePlaceholder}
      className={className}
      variant={variant}
      open={open}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      maxWidth="680px"
      search={search}
      onSearchChange={(e) => setSearch(e.target.value)}
      searchPlaceholder={window.innerWidth < 640 ? t('form.select_hospital') : t('common.search_hospital_long')}
      title={t('common.main_hospital_quick')}
    >
      <div className="flex flex-col sm:flex-row h-full" style={{ minHeight: '240px', maxHeight: window.innerWidth >= 640 ? 'calc(80vh - 160px)' : undefined }}>
        {/* Mobile: 가로 탭 */}
        {!search.trim() && (
          <div className="sm:hidden flex border-b border-mono-100 overflow-x-auto scrollbar-hide px-3 py-1.5 gap-1.5 shrink-0">
            {regionSidebar.map((region) => {
              const isActive = region.id === activeRegion;
              return (
                <button
                  key={region.id}
                  type="button"
                  onClick={() => setActiveRegion(region.id)}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                    isActive ? 'bg-orange-500 text-white' : 'bg-mono-100 text-mono-500'
                  }`}
                >
                  {region.label}
                </button>
              );
            })}
          </div>
        )}
        {/* PC: 세로 사이드바 */}
        {!search.trim() && (
          <div className="hidden sm:block w-[120px] flex-shrink-0 bg-mono-50 border-r border-mono-100 overflow-y-auto overscroll-contain py-2">
            {regionSidebar.map((region) => {
              const isActive = region.id === activeRegion;
              return (
                <button
                  key={region.id}
                  type="button"
                  onClick={() => setActiveRegion(region.id)}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                    isActive
                      ? 'text-orange-500 font-bold border-l-2 border-orange-500 bg-white'
                      : 'text-mono-500 hover:text-mono-700 border-l-2 border-transparent'
                  }`}
                >
                  {region.label}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex-1 overflow-y-auto overscroll-contain p-3 sm:p-5">
          {filteredData.mode === 'search' && (
            <>
              {/* 주요 병원 결과 */}
              {filteredData.results.length > 0 && (
                <div className="mb-6">
                  <div className="text-xs font-bold text-mono-400 mb-3">{t('common.main_hospitals')} ({filteredData.results.length})</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-0.5">
                    {filteredData.results.map(renderItem)}
                  </div>
                </div>
              )}

              {/* 카카오 검색 결과 (HP8 병원 필터) */}
              {kakaoLoading && (
                <p className="text-sm text-mono-400 text-center py-4">{t('common.searching')}</p>
              )}
              {!kakaoLoading && kakaoResults.length > 0 && (
                <div>
                  <div className="text-xs font-bold text-mono-400 mb-3">{t('common.search_results_label')} ({kakaoResults.length})</div>
                  <div className="flex flex-col gap-0.5">
                    {kakaoResults.map((result) => (
                      <button
                        key={result.name + result.address}
                        type="button"
                        onClick={() => handleKakaoSelect(result)}
                        className="text-left px-3 py-2.5 rounded-lg transition-colors hover:bg-mono-50"
                      >
                        <span className="text-sm text-mono-900">{result.nameI18n || result.name}</span>
                        <span className="text-xs text-mono-400 ml-2">{result.addressI18n || result.address}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 결과 없음 */}
              {filteredData.results.length === 0 && !kakaoLoading && kakaoResults.length === 0 && (
                <p className="text-sm text-mono-400 text-center py-8">{t('common.no_search_results')}</p>
              )}
            </>
          )}

          {filteredData.mode === 'grouped' && (
            hospitalGroups.map((group) => (
              <div key={group.key} className="mb-6 last:mb-0">
                <div className="text-base font-bold text-mono-900 mb-3 pb-2 border-b border-mono-100">
                  {regionLabels[group.label] || group.label}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-0.5">
                  {group.items.map(renderItem)}
                </div>
              </div>
            ))
          )}

          {filteredData.mode === 'single' && (
            <>
              <div className="text-base font-bold text-mono-900 mb-3 pb-2 border-b border-mono-100">
                {regionLabels[filteredData.label] || filteredData.label}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-0.5">
                {filteredData.items.map(renderItem)}
              </div>
            </>
          )}
        </div>
      </div>
    </SelectorOverlay>

      {showRestrictionModal && (
        <ModalWrapper onClose={() => setShowRestrictionModal(false)} variant="compact" gap="gap-4">
          <div className="flex items-center justify-center relative">
            <h2 className="text-lg font-bold text-mono-900">{t('common.guide')}</h2>
            <div className="absolute right-0">
              <ModalCloseButton onClick={() => setShowRestrictionModal(false)} />
            </div>
          </div>
          <p className="text-sm text-mono-700 text-center py-4">{t('common.region_inquiry')}</p>
          <div className="flex gap-3">
            <Button level="level3" fullWidth onClick={() => setShowRestrictionModal(false)}>{t('common.cancel')}</Button>
            <Button level="level1" fullWidth onClick={() => { setShowRestrictionModal(false); navigate('/inquiry'); }}>{t('common.confirm')}</Button>
          </div>
        </ModalWrapper>
      )}
    </>
  );
}
